/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../../../../assets/Icons";
import AmountToWords from "../../../../components/_utils/AmountToWords.js";
import CommonAssignLabelPopup from "../../../../components/_utils/CommonPopups/CommonAssignLabelPopup/index.jsx";
import SmallPopup from "../../../../components/_utils/CommonSmallPopup";
import PrimaryStepper from "../../../../components/_utils/CommonSteppers/PrimaryStepper";
import CommonToast from "../../../../components/_utils/CommonToast";
import TopNavComponent from "../../../../components/_utils/CommonTopNav/TopNavComponent/index.jsx";
import InternalAccountPopUp from "../../../../components/Accounts/InternalAccount/InternalAccountPopUp/index.jsx";
import Loader from "../../../../components/Loader/Index.jsx";
import ScheduleInternalTransactionStepAdditionalDetails from "../../../../components/Transactions/ScheduleInternalTransaction/ScheduleInternalTransactionCreate/ScheduleInternalTransactionStepAdditionalDetails/index.jsx";
import ScheduleInternalTransactionStepAmount from "../../../../components/Transactions/ScheduleInternalTransaction/ScheduleInternalTransactionCreate/ScheduleInternalTransactionStepAmount";
import ScheduleInternalTransactionStepConfirm from "../../../../components/Transactions/ScheduleInternalTransaction/ScheduleInternalTransactionCreate/ScheduleInternalTransactionStepConfirm";
import ScheduleInternalTransactionStepFrequency from "../../../../components/Transactions/ScheduleInternalTransaction/ScheduleInternalTransactionCreate/ScheduleInternalTransactionStepFrequency";
import ScheduleInternalTransactionStepSelectAccounts from "../../../../components/Transactions/ScheduleInternalTransaction/ScheduleInternalTransactionCreate/ScheduleInternalTransactionStepSelectAccounts";
import ScheduleInternalTransactionViewAllPopup from "../../../../components/Transactions/ScheduleInternalTransaction/ScheduleInternalTransactionViewallpopup/index.jsx";
import {
  ownerAccountsViewAll,
  resetOwnerBalance,
} from "../../../../redux/features/owner/ownerAllVerification/ownerAllVerification.js";
import {
  getAllCategories,
  uploadFile,
} from "../../../../redux/features/sharedResources/sharedResources";
import {
  scheduledInternalCreate,
  scheduledInternalOccurrences,
  scheduledInternalViewAll,
} from "../../../../redux/features/transactions/scheduleInternalTransactions/index.js";
import {
  GlobalConfiguration,
  IntervalConfiguration,
  PaginationConfiguration,
  PaymentMethodAnimationConfiguration,
  ScreenSizeConfiguration,
} from "../../../../utils/configurationProvider";
import {
  FREQUENCY_OPTIONS,
  MONTHS,
  SLOT_DATA,
  WEEKDAYS,
} from "../../../../utils/dateConstants.js";
import globalText from "../../../../utils/globalText";
import Validation from "../../../../utils/validation.js";

/*                          CONSTANTS & PURE HELPERS                          */

const {
  lengthCheckZero: ZERO,
  lengthCheckOne: ONE,
  lengthCheckTwo: TWO,
  lengthCheckThree: THREE,
  lengthCheckFive: FIVE,
} = GlobalConfiguration;

const RTGS_MIN_AMOUNT = 200000;
const MAX_NUMERIC_DIGITS = 15;
const HALF_YEARLY_LABEL = "Half yearly";
const HALF_YEARLY_KEY = "half_yearly";

const COMMON_STEPPER_CONFIG = {
  previousEnableBgColor: "var(--primary-nav-act)",
  previousDisableBgColor: "var(--text-disabled)",
  previousEnableTextColor: "var(--text-primary)",
  previousHoverOption: "white",
  nextHoverOption: "black",
};

// how far the default "end date" is from the start date, per frequency
const FREQUENCY_END_DATE_OFFSET = {
  Weekly: { unit: "date", amount: 7 },
  Fortnightly: { unit: "date", amount: 14 },
  Monthly: { unit: "month", amount: 1 },
  Quarterly: { unit: "month", amount: 3 },
  [HALF_YEARLY_LABEL]: { unit: "month", amount: 6 },
  Yearly: { unit: "year", amount: 1 },
};

const omit = (obj, ...keys) =>
  Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keys.includes(key)),
  );

const uniq = (...lists) => [...new Set(lists.flatMap((list) => list || []))];

const isValidHttpUrl = (url) =>
  /^(https?:\/\/)([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(:[0-9]{1,5})?(\/.*)?$/.test(
    url,
  );

/** Splits a flat label list into owner labels / other labels. */
const splitLabels = (allLabels = [], ownerLabels = []) => ({
  ownerLabels: allLabels.filter((label) => ownerLabels.includes(label)),
  labels: allLabels.filter((label) => !ownerLabels.includes(label)),
});

const getAmountWords = (amount) => {
  const amountNumber = parseFloat(amount);
  const words = AmountToWords(amountNumber);
  return amountNumber > ZERO && amountNumber <= ONE
    ? words.replace("rupees", "rupee")
    : words;
};

/** "Half yearly" -> "half_yearly", "Monthly" -> "monthly". */
const toApiFrequency = (frequency) =>
  frequency === HALF_YEARLY_LABEL
    ? HALF_YEARLY_KEY
    : (frequency || "").toLowerCase();

const parseToDateOrNull = (value) => {
  if (!value) return null;
  const candidate = new Date(value);
  return isNaN(candidate) ? null : candidate;
};

/** Date -> "YYYY-MM-DD" in the user's local timezone. */
const toLocalIsoDate = (date) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (isNaN(parsed)) return "";
  return new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
};

/** Date -> "DD-MM-YYYY" in the user's local timezone (API payload format). */
const toLocalDayFirstDate = (date) =>
  toLocalIsoDate(date).split("-").reverse().join("-");

/** Date -> "5 Jan, 2026" (display format). */
const formatDisplayDate = (date) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (isNaN(parsed)) return "";
  return `${parsed.getDate()} ${parsed.toLocaleString("default", { month: "short" })}, ${parsed.getFullYear()}`;
};

const isToday = (date) => {
  if (!date) return false;
  const today = new Date();
  const target = new Date(date);
  return (
    today.getDate() === target.getDate() &&
    today.getMonth() === target.getMonth() &&
    today.getFullYear() === target.getFullYear()
  );
};

/** If the schedule starts today, only slots later than the current hour are valid. */
const getAvailableSlots = (startDate) =>
  isToday(startDate)
    ? SLOT_DATA.filter(
        (slot) => Number(slot.value.split(":")[0]) > new Date().getHours(),
      )
    : SLOT_DATA;

const getFrequencyEndDate = (startDate, frequency) => {
  if (!startDate) return new Date();

  const endDate = new Date(startDate);
  const { unit, amount } = FREQUENCY_END_DATE_OFFSET[frequency] || {
    unit: "date",
    amount: 1,
  };

  if (unit === "date") endDate.setDate(endDate.getDate() + amount);
  if (unit === "month") endDate.setMonth(endDate.getMonth() + amount);
  if (unit === "year") endDate.setFullYear(endDate.getFullYear() + amount);

  return endDate;
};

const isMandatoryFieldFilled = ({ data_type: dataType, value }) =>
  dataType === "from_year_to_year" ? !!(value?.from && value?.to) : !!value;

/** Whether an entered value should be sent to the API for the given field type. */
const isFieldValueProvided = (dataType, value) => {
  switch (dataType) {
    case "numeric":
      return !(value === "" || value === undefined || value === null);
    case "quantity":
      return !!value && Number(value) > ZERO;
    case "multi_select_list":
      return Array.isArray(value) && value.length > ZERO;
    case "date_range":
      return !!(value?.start_date && value?.end_date);
    case "from_year_to_year":
      return !!(value?.from && value?.to);
    default:
      return !(
        value === "" ||
        value === undefined ||
        value === null ||
        value === "Not given"
      );
  }
};

/** Category config fields + entered values + upload responses -> API payload. */
const buildAdditionalDataPayload = (
  fields = [],
  additionalData = {},
  uploadResponses = [],
) => {
  let uploadIndex = ZERO;

  return fields.map((field) => {
    const { data_type: dataType, name } = field;
    const value = additionalData?.[name];

    if (dataType === "upload") {
      if (!value || value === "Not given") return omit(field, "description");
      return {
        ...omit(field, "description"),
        value: uploadResponses[uploadIndex++]?.payload,
      };
    }

    if (dataType === "toggle") {
      return value === true
        ? { ...omit(field, "description"), value: true }
        : omit(field, "description");
    }

    if (!isFieldValueProvided(dataType, value)) return omit(field, "value");

    return {
      ...field,
      value:
        dataType === "numeric" || dataType === "quantity"
          ? Number(value)
          : value,
    };
  });
};

/** Category config fields + entered values -> mergeCategory (drives validation + confirm step). */
const buildMergeCategory = (fields = [], additionalData = {}) =>
  fields.map((field) => {
    const { data_type: dataType, name, mandatory } = field;
    const value = additionalData[name];

    const hasValue =
      value !== undefined &&
      (Array.isArray(value) ? value.length > ZERO : value !== "");

    if (hasValue) {
      return {
        ...field,
        value: dataType === "numeric" ? String(value) : value,
      };
    }

    switch (dataType) {
      case "multi_select_list":
        return { ...field, value: [] };
      case "toggle":
        return { ...field, value: false };
      case "hyperlink":
      case "upload":
        return { ...field, value: "" };
      default:
        return mandatory !== "Y" ? { ...field, value: "" } : field;
    }
  });

/** Raw input from a field component -> value stored in additionalData. */
const toAdditionalDataValue = (dataType, value) => {
  switch (dataType) {
    case "month_year": {
      if (!(value?.month && value?.year)) return "";
      const monthNumber = String(MONTHS.indexOf(value.month) + ONE).padStart(
        2,
        "0",
      );
      return `${monthNumber}/${value.year}`;
    }

    case "from_year_to_year": {
      const toNumberOrNull = (v) =>
        v !== "" && v !== undefined ? Number(v) : null;
      return {
        from: toNumberOrNull(value?.from),
        to: toNumberOrNull(value?.to),
      };
    }

    case "date_range":
      return value?.start_date && value?.end_date
        ? { start_date: value.start_date, end_date: value.end_date }
        : null;

    case "numeric":
      return value === ""
        ? ""
        : Number(String(value).replace(/\D/g, "").slice(0, MAX_NUMERIC_DIGITS));

    case "quantity":
      return Number(value);

    default:
      return typeof value === "string" ? value.replace(/^\s+/, "") : value;
  }
};

/*                                 COMPONENT                                  */

const ScheduleInternalTransactionCreate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const yearDropdownRef = useRef(null);
  const labelActionDispatchedRef = useRef(false);

  const currentAccountDetails = location?.state;

  /* -------------------------------- state -------------------------------- */
  const [activeStep, setActiveStep] = useState(0);
  const [stepDirection, setStepDirection] = useState(null);
  const [isStepAnimationComplete, setIsStepAnimationComplete] = useState(true);
  const [isNextEnabled, setIsNextEnabled] = useState(false);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= ScreenSizeConfiguration.mobileScreen,
  );

  const [isNeft, setIsNeft] = useState(false);
  const [isRtgs, setIsRtgs] = useState(false);
  const [slideAnimation, setSlideAnimation] = useState(false);

  const [isConfidential, setIsConfidential] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTransactionLoading, setIsTransactionLoading] = useState(false);
  const [hasSimilarTransaction, setHasSimilarTransaction] = useState(false);

  // account step
  const [isInternalAccountModalOpen, setIsInternalAccountModalOpen] =
    useState(false);
  const [isOwnerAccountModalOpen, setIsOwnerAccountModalOpen] = useState(false);

  // additional-details step
  const [additionalData, setAdditionalData] = useState({});
  const [quantityValues, setQuantityValues] = useState({});
  const [timeValues, setTimeValues] = useState({});
  const [startDateValues, setStartDateValues] = useState({});
  const [endDateValues, setEndDateValues] = useState({});
  const [hyperlinkValues, setHyperlinkValues] = useState({});
  const [yearRangeValues, setYearRangeValues] = useState({});
  const [monthYearValues, setMonthYearValues] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [previewImages, setPreviewImages] = useState({});

  // frequency step
  const [isFrequencyDropdownOpen, setIsFrequencyDropdownOpen] = useState(false);
  const [isSlotDropdownOpen, setIsSlotDropdownOpen] = useState(false);
  const [isStartDatePickerOpen, setIsStartDatePickerOpen] = useState(false);
  const [isEndDatePickerOpen, setIsEndDatePickerOpen] = useState(false);
  const [isOccurrencesModalOpen, setIsOccurrencesModalOpen] = useState(false);
  const [scheduleOccurrenceDates, setScheduleOccurrenceDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState(SLOT_DATA || []);

  // popups
  const [isLabelModalOpen, setIsLabelModalOpen] = useState(false);
  const [isLabelLoading, setIsLabelLoading] = useState(false);
  const [isSimilarModalOpen, setIsSimilarModalOpen] = useState(false);
  const [isConfirmLeavePopupOpen, setIsConfirmLeavePopupOpen] = useState(false);

  const [txnPayLoad, setTxnPayLoad] = useState({
    bankAccountName: currentAccountDetails?.bank_account_holder_full_name || "",
    beneAccountId: currentAccountDetails?.id || null,
    bankAccountIFSC: currentAccountDetails?.ifsc_code || "",
    bankAccountNo: currentAccountDetails?.bank_acc_no || "",
    amount: "",
    settleType: "",
    bankLogo: currentAccountDetails?.bank_name || "",
    pan: currentAccountDetails?.pan || "",
    ownerPan: null,
    ownerId: null,
    ownerBankIFSC: null,
    ownerBankBalance: 0,
    ownerLastUpdated: null,
    ownerBankLogo: null,
    ownerBankAccountNo: null,
    ownerBankAccountName: null,
    selectedCategory: null,
    mergeCategory: null,
    amountInWords: "",
    destinationAccountBalance: 0,
    destinationAcountOwnerId: null,
    isDestinationOwnerActive: true,
    destinationAcountOwnerLastUpdated: null,
    scheduled_name: "",
    frequency: "",
    transaction_start_date: "",
    transaction_end_date: "",
    slot: "",
    occurrences: "",
  });

  const [frequencyStartDate, setFrequencyStartDate] = useState(
    parseToDateOrNull(txnPayLoad.transaction_start_date),
  );
  const [frequencyEndDate, setFrequencyEndDate] = useState(
    parseToDateOrNull(txnPayLoad.transaction_end_date),
  );

  const [selectedLabels, setSelectedLabels] = useState(() =>
    uniq(txnPayLoad.labels, txnPayLoad.ownerLabels),
  );
  const [availableLabels, setAvailableLabels] = useState(() =>
    uniq(txnPayLoad.labels, txnPayLoad.ownerLabels),
  );

  /* ------------------------------- selectors ------------------------------ */
  const services = useSelector((state) => state.refreshToken.services);
  const { categoriesData } = useSelector((state) => state.sharedResources);

  /* ------------------------------- derived -------------------------------- */
  const internalCategory = categoriesData?.categories?.find(
    (category) => category.name === "Internal",
  );

  const steps = [
    globalText?.SOURCE_AND_DESTINATION_ACCOUNT,
    globalText?.ADDITIONAL_DETAILS,
    globalText?.AMOUNT,
    globalText?.FREQUENCY,
    globalText?.CONFIRM_AND_CREATE,
  ];
  const lastStepIndex = steps.length - ONE;

  const stepAnimationClass =
    stepDirection === "next" ? "animate-slide-next" : "animate-slide-prev";

  /* ------------------------------ small helpers --------------------------- */
  const triggerStepAnimation = (stepDirection) => {
    setStepDirection(stepDirection);
    setIsStepAnimationComplete(false);
    setTimeout(
      () => setIsStepAnimationComplete(true),
      IntervalConfiguration.intervalMedium,
    );
  };

  const showAmountLimitToast = (comparison) =>
    CommonToast({
      text: (
        <>
          Transaction amount should be {comparison} <br /> INR 2 lakhs.
        </>
      ),
      icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
      variant: "var(--error)",
    });

  /* ------------------------------ create / next --------------------------- */
  const buildDuplicateCheckPayload = () => ({
    page_no: ONE,
    page_item_count: PaginationConfiguration?.paginationSmall,
    amount: txnPayLoad?.amount,
    payment_method: txnPayLoad?.settleType?.toUpperCase(),
    internal_account_id: txnPayLoad?.beneAccountId,
    owner_account_id: txnPayLoad?.ownerId,
    confidential_transaction: isConfidential
      ? "Show only confidential"
      : "Hide confidential",
  });

  const handleCreateScheduledTransaction = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const hasUploads = Object.keys(uploadedFiles || {}).length > ZERO;
      const uploadResponses = hasUploads
        ? await Promise.all(
            Object.values(uploadedFiles).map((file) =>
              dispatch(uploadFile({ file })),
            ),
          )
        : [];

      const scheduledInternalTxnData = {
        amount: txnPayLoad.amount,
        internal_account_id: txnPayLoad.beneAccountId,
        owner_account_id: txnPayLoad.ownerId,
        category_id: txnPayLoad?.selectedCategory?.id,
        payment_method: txnPayLoad.settleType?.toUpperCase(),
        frequency: toApiFrequency(txnPayLoad.frequency),
        confidential_transaction: isConfidential,
        occurrences: txnPayLoad.occurrences,
        type_of_transactions: "Internal Transaction",
        transaction_start_date: toLocalDayFirstDate(
          txnPayLoad.transaction_start_date,
        ),
        transaction_end_date: toLocalDayFirstDate(
          txnPayLoad.transaction_end_date,
        ),
        slot: txnPayLoad.slot,
        scheduled_name: txnPayLoad.scheduled_name,
        additional_data: {
          data: buildAdditionalDataPayload(
            txnPayLoad?.selectedCategory?.configuration?.data,
            additionalData,
            uploadResponses,
          ),
        },
      };

      const response = await dispatch(
        scheduledInternalCreate({ scheduledInternalTxnData }),
      );

      if (response?.payload?.status === true) {
        navigate(
          `/internal_transaction_dashboard/create_schedule_transaction/success/${response?.payload?.transaction?.[0]?.txn_id}`,
        );
      } else {
        navigate(
          "/internal_transaction_dashboard/create_schedule_transaction/failed",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    // amount -> frequency step: run the duplicate check before moving on
    if (activeStep === THREE) {
      setIsSubmitting(true);
      setIsTransactionLoading(true);

      const response = await dispatch(
        scheduledInternalViewAll(buildDuplicateCheckPayload()),
      );
      const hasDuplicates = response?.payload?.transactions?.length > ZERO;

      setHasSimilarTransaction(hasDuplicates);
      setIsSubmitting(false);

      if (hasDuplicates) {
        toast.dismiss();
        CommonToast({
          text: "Few issues found click check to view.",
          icon: ICONS?.INFO_FILLED,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      }

      if (response?.payload?.status) setActiveStep((prev) => prev + ONE);
      setIsTransactionLoading(false);
      triggerStepAnimation("next");
      return;
    }

    if (activeStep < lastStepIndex) {
      setActiveStep((prev) => prev + ONE);
      triggerStepAnimation("next");
      return;
    }

    if (hasSimilarTransaction) {
      setIsSimilarModalOpen(true);
      return;
    }
    await handleCreateScheduledTransaction();
  };

  const isFormDirty = () =>
    activeStep > ZERO ||
    txnPayLoad.ownerId !== null ||
    isConfidential !== false ||
    txnPayLoad.scheduled_name !== "";

  const leaveOrConfirm = () => {
    if (isFormDirty()) setIsConfirmLeavePopupOpen(true);
    else navigate(-1);
  };

  const handleBack = () => {
    if (activeStep === ZERO) {
      leaveOrConfirm();
      return;
    }
    setActiveStep((prev) => Math.max(prev - ONE, ZERO));
    triggerStepAnimation("previous");
  };

  /* ------------------------------- accounts ------------------------------- */
  const handleOwnerSelection = (name, accountNumber, ifsc, logo, ownerId) =>
    setTxnPayLoad((prev) => ({
      ...prev,
      ownerId,
      ownerAccountName: name,
      ownerBankAccountNo: accountNumber,
      ownerBankIFSC: ifsc,
      ownerBankLogo: logo,
    }));

  const handleInternalAccountSelection = async (dataItem) => {
    if (!dataItem?.id) return;

    const {
      id: internalAccountId,
      name: accountName,
      bank_acc_no: accountNumber,
      ifsc_code: ifsc,
      bank_name: bankLogo,
      pan_no: panNo,
      internal_account: internalAccount,
    } = dataItem;

    const ownerAccountsResponse = await dispatch(
      ownerAccountsViewAll({
        bank_account_no: accountNumber,
        pan: panNo,
        ifsc,
      }),
    );
    const destinationOwner =
      ownerAccountsResponse?.payload?.owner_accounts?.[0];

    setTxnPayLoad((prev) => ({
      ...prev,
      beneAccountId: internalAccountId,
      bankAccountName: accountName,
      bankAccountNo: accountNumber,
      bankAccountIFSC: ifsc,
      bankLogo,
      pan: panNo,
      isDestinationOwnerActive: internalAccount,
      destinationAccountBalance: destinationOwner?.owner_account_balance,
      destinationAcountOwnerId: destinationOwner?.id,
      destinationAcountOwnerLastUpdated:
        destinationOwner?.owner_account_balance_last_fetched_at_in_words,
      selectedCategory: prev.selectedCategory || internalCategory,
      mergeCategory: prev.mergeCategory?.length
        ? prev.mergeCategory
        : internalCategory?.configuration?.data,
    }));

    setIsInternalAccountModalOpen(false);
  };

  /* -------------------------------- scheduled Name -------------------------------- */
  const handleScheduleNameChange = (scheduledName) =>
    setTxnPayLoad((prev) => ({ ...prev, scheduled_name: scheduledName }));

  /* -------------------------------- labels -------------------------------- */
  const handleCloseLabelModal = (finalLabels) => {
    setIsLabelModalOpen(false);
    if (!finalLabels) return;

    setTxnPayLoad((prev) => ({
      ...prev,
      ...splitLabels(finalLabels, prev.ownerLabels),
    }));
  };

  /* -------------------------------- amount -------------------------------- */
  const handleAmountChange = (input) => {
    const { name, value } = input?.target ? input.target : input;
    const trimmedValue = value?.toString()?.trim() ?? "";

    if (!trimmedValue) {
      setTxnPayLoad((prev) => ({ ...prev, amount: "", amountInWords: "" }));
      return;
    }
    if (trimmedValue[0] === "0") return;

    const numericValue = trimmedValue.replace(/[^0-9.]/g, "");

    if (
      name !== "amount" ||
      (!Validation.allowedAmount(numericValue) && numericValue.length)
    ) {
      return;
    }

    const settleType =
      parseFloat(numericValue) > RTGS_MIN_AMOUNT ? "rtgs" : "neft";
    const hasPaymentMethodChanged =
      (settleType === "neft" && isRtgs) || (settleType === "rtgs" && isNeft);

    if (hasPaymentMethodChanged) {
      setSlideAnimation(true);
      setTimeout(
        () => setSlideAnimation(false),
        PaymentMethodAnimationConfiguration.slideDuration,
      );
    }

    setIsNeft(settleType === "neft");
    setIsRtgs(settleType === "rtgs");
    setTxnPayLoad((prev) => ({
      ...prev,
      amount: numericValue,
      amountInWords: getAmountWords(numericValue),
      settleType,
    }));
  };

  const handleNeftClick = () => {
    if (Number(txnPayLoad?.amount) > RTGS_MIN_AMOUNT)
      showAmountLimitToast("less than");
  };

  const handleRtgsClick = () => {
    if (Number(txnPayLoad?.amount) <= RTGS_MIN_AMOUNT)
      showAmountLimitToast("more than");
  };

  /* ----------------------------- additional details ----------------------- */
  const handleInputChange = (event) => {
    event?.preventDefault?.();

    const {
      name,
      value,
      data_type: dataTypeFromEvent,
    } = event?.target ?? event;

    const field = txnPayLoad?.selectedCategory?.configuration?.data?.find(
      (item) => item.name === name,
    );
    if (!field) return;

    const dataType = dataTypeFromEvent || field.data_type;

    setAdditionalData((prev) => ({
      ...prev,
      [name]: toAdditionalDataValue(dataType, value),
    }));
  };

  /* ------------------------------- frequency ------------------------------ */
  const handleFetchOccurrences = async () => {
    const response = await dispatch(
      scheduledInternalOccurrences({
        frequency: toApiFrequency(txnPayLoad?.frequency),
        transaction_start_date: toLocalIsoDate(
          txnPayLoad?.transaction_start_date,
        ),
        transaction_end_date: toLocalIsoDate(txnPayLoad?.transaction_end_date),
        navigate,
        services,
      }),
    );

    setTxnPayLoad((prev) => ({
      ...prev,
      occurrences: response?.payload?.occurrences,
    }));
    setScheduleOccurrenceDates(response?.payload?.dates ?? []);
  };

  const handleStartDateConfirm = () => {
    setAvailableSlots(getAvailableSlots(frequencyStartDate));
    setIsStartDatePickerOpen(false);

    const formattedStartDate = toLocalIsoDate(frequencyStartDate);
    setTxnPayLoad((prev) => ({
      ...prev,
      transaction_start_date: formattedStartDate,
      ...(prev.frequency === "Once" && {
        transaction_end_date: formattedStartDate,
      }),
      slot: "",
    }));
  };

  const handleEndDateConfirm = () => {
    setIsEndDatePickerOpen(false);
    setTxnPayLoad((prev) => ({
      ...prev,
      transaction_end_date: toLocalIsoDate(frequencyEndDate),
    }));
  };

  // shared by both pickers: cancelling either one clears the whole date range
  const resetFrequencyDates = () => {
    setFrequencyStartDate(null);
    setFrequencyEndDate(null);
    setScheduleOccurrenceDates([]);
    setTxnPayLoad((prev) => ({
      ...prev,
      transaction_start_date: null,
      transaction_end_date: null,
      occurrences: "",
    }));
  };

  const handleStartDateCancel = () => {
    setIsStartDatePickerOpen(false);
    resetFrequencyDates();
  };

  const handleEndDateCancel = () => {
    setIsEndDatePickerOpen(false);
    resetFrequencyDates();
  };

  const getEndDate = () =>
    getFrequencyEndDate(
      txnPayLoad?.transaction_start_date,
      txnPayLoad?.frequency,
    );

  /* -------------------------------- effects ------------------------------- */
  useEffect(() => {
    dispatch(getAllCategories());
  }, [dispatch]);

  // default the category to "Internal" once categories are loaded
  useEffect(() => {
    if (!internalCategory) return;

    setTxnPayLoad((prev) =>
      prev.selectedCategory === null && prev.mergeCategory === null
        ? {
            ...prev,
            selectedCategory: internalCategory,
            mergeCategory: internalCategory?.configuration?.data,
          }
        : prev,
    );
  }, [internalCategory]);

  // entered values -> mergeCategory
  useEffect(() => {
    const fields = txnPayLoad.selectedCategory?.configuration?.data;
    if (!additionalData || !fields) return;

    setTxnPayLoad((prev) => ({
      ...prev,
      mergeCategory: buildMergeCategory(fields, additionalData),
    }));
  }, [additionalData, txnPayLoad.selectedCategory]);

  // duplicate-check refresh on the confirm step
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeStep === lastStepIndex) {
        dispatch(scheduledInternalViewAll(buildDuplicateCheckPayload()));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [txnPayLoad]);

  useEffect(() => {
    if (frequencyStartDate) handleStartDateConfirm();
  }, [frequencyStartDate]);

  useEffect(() => {
    if (frequencyEndDate) handleEndDateConfirm();
  }, [frequencyEndDate]);

  // slots + occurrences depend on the chosen schedule
  useEffect(() => {
    setAvailableSlots(getAvailableSlots(frequencyStartDate));

    if (
      txnPayLoad?.transaction_start_date &&
      txnPayLoad?.transaction_end_date &&
      txnPayLoad?.frequency
    ) {
      handleFetchOccurrences();
    }
  }, [
    txnPayLoad?.transaction_start_date,
    txnPayLoad?.transaction_end_date,
    txnPayLoad?.frequency,
  ]);

  // enable / disable "Next"
  useEffect(() => {
    const {
      beneAccountId,
      ownerId,
      scheduled_name: scheduledName,
      amount,
      settleType,
      selectedCategory,
      mergeCategory,
      occurrences,
      slot,
      transaction_start_date: transactionStartDate,
      transaction_end_date: transactionEndDate,
      frequency,
    } = txnPayLoad;

    const mandatoryFields = (
      selectedCategory?.configuration?.data || []
    ).filter((field) => field?.mandatory === "Y");
    const filledMandatoryCount = (mergeCategory || []).filter(
      (field) => field?.mandatory === "Y" && isMandatoryFieldFilled(field),
    ).length;
    const hyperlinksValid = (mergeCategory || [])
      .filter(
        (field) => field?.data_type === "hyperlink" && field?.value !== "",
      )
      .every((field) => isValidHttpUrl(field?.value));

    const isOverRtgsLimit = amount > RTGS_MIN_AMOUNT;
    const isRtgsInvalid = isRtgs && !isOverRtgsLimit;
    const isNeftInvalid = isNeft && isOverRtgsLimit;
    const isPaymentMethodInvalid =
      ((settleType === "imps" || settleType === "neft") && isOverRtgsLimit) ||
      (settleType === "rtgs" && !isOverRtgsLimit);

    const stepValidity = [
      !!beneAccountId &&
        !!ownerId &&
        scheduledName?.replace(/\s+/g, "").trim().length >= FIVE,
      !mandatoryFields.length ||
        (filledMandatoryCount === mandatoryFields.length && hyperlinksValid),
      !!amount && !isRtgsInvalid && !isNeftInvalid && !isPaymentMethodInvalid,
      !!occurrences &&
        !!slot &&
        !!transactionStartDate &&
        !!transactionEndDate &&
        !!frequency,
    ];

    setIsNextEnabled(stepValidity[activeStep] ?? true);
  }, [activeStep, txnPayLoad, isRtgs, isNeft, hyperlinkValues]);

  useEffect(() => {
    const handleResize = () =>
      setIsMobile(window.innerWidth <= ScreenSizeConfiguration.mobileScreen);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Balance refresh (request, toasts, payload update) is handled inside
  // CommonDestinationAccount. We only clear the stale refresh result on unmount.
  useEffect(() => {
    return () => {
      dispatch(resetOwnerBalance());
    };
  }, []);

  /* ------------------------------ stepper data ---------------------------- */
  const dataArray = steps.map((stepLabel, index) => {
    const isLastStep = index === lastStepIndex;
    const isTxnLoading = isLastStep && isSubmitting;

    return {
      ...COMMON_STEPPER_CONFIG,
      previousText: index === ZERO ? "Cancel" : "Previous",
      previousIsDisabled: isTxnLoading,
      nextText: isLastStep
        ? hasSimilarTransaction
          ? "Check"
          : "Create"
        : "Next",
      nextLoaderNeeded: isLastStep,
      nextIsLoading: isTxnLoading,
      nextIsEnabled: isLastStep ? !isSubmitting : isNextEnabled,
      nextEnableBgColor: isLastStep
        ? hasSimilarTransaction
          ? "var(--emphasis)"
          : "var(--positive-act)"
        : "var(--text-primary)",
      nextDisableBgColor: isLastStep
        ? "var(--positive-act)"
        : "var(--text-disabled)",
      nextEnableTextColor: isLastStep
        ? "var(--text-primary)"
        : "var(--primary-bg)",
      nextDisableTextColor: "var(--text-primary)",
      stepperText: stepLabel,
    };
  });

  /* ------------------------------- renderers ------------------------------ */
  const renderStep = () => {
    switch (activeStep) {
      case ZERO:
        return (
          <ScheduleInternalTransactionStepSelectAccounts
            txnPayLoad={txnPayLoad}
            setTxnPayLoad={setTxnPayLoad}
            openOwner={isOwnerAccountModalOpen}
            setOpenOwner={setIsOwnerAccountModalOpen}
            isConfidential={isConfidential}
            setIsConfidential={setIsConfidential}
            handleOwnerSelection={handleOwnerSelection}
            handleOpenInternalAccountModal={() =>
              setIsInternalAccountModalOpen(true)
            }
            handleScheduleNameChange={handleScheduleNameChange}
          />
        );

      case ONE:
        return (
          <ScheduleInternalTransactionStepAdditionalDetails
            txnPayLoad={txnPayLoad}
            additionalData={additionalData}
            setAdditionalData={setAdditionalData}
            timeData={timeValues}
            setTimeData={setTimeValues}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            startDate={startDateValues}
            endDate={endDateValues}
            setStartDate={setStartDateValues}
            setEndDate={setEndDateValues}
            selectedValue={yearRangeValues}
            setSelectedValue={setYearRangeValues}
            selectedValueMonth={monthYearValues}
            setSelectedValueMonth={setMonthYearValues}
            previewImages={previewImages}
            setPreviewImages={setPreviewImages}
            quantities={quantityValues}
            setQuantities={setQuantityValues}
            hyperLinks={hyperlinkValues}
            setHyperLinks={setHyperlinkValues}
            handleInputChange={handleInputChange}
          />
        );

      case TWO:
        return (
          <ScheduleInternalTransactionStepAmount
            slideAnimation={slideAnimation}
            txnPayLoad={txnPayLoad}
            isRtgs={isRtgs}
            handleRtgsClick={handleRtgsClick}
            handleNeftClick={handleNeftClick}
            handleAmountChange={handleAmountChange}
          />
        );

      case THREE:
        return (
          <ScheduleInternalTransactionStepFrequency
            txnPayLoad={txnPayLoad}
            setTxnPayLoad={setTxnPayLoad}
            openOccurrencesModal={isOccurrencesModalOpen}
            setOpenOccurrencesModal={setIsOccurrencesModalOpen}
            scheduleOccurrenceDates={scheduleOccurrenceDates}
            frequencyOptions={FREQUENCY_OPTIONS}
            frequencyStartDate={frequencyStartDate}
            frequencyEndDate={frequencyEndDate}
            setFrequencyStartDate={setFrequencyStartDate}
            setFrequencyEndDate={setFrequencyEndDate}
            showDatePicker={isStartDatePickerOpen}
            setShowDatePicker={setIsStartDatePickerOpen}
            showEndDatePicker={isEndDatePickerOpen}
            setShowEndDatePicker={setIsEndDatePickerOpen}
            dropdownOpen={isFrequencyDropdownOpen}
            setDropdownOpen={setIsFrequencyDropdownOpen}
            slotDropdownOpen={isSlotDropdownOpen}
            setSlotDropdownOpen={setIsSlotDropdownOpen}
            availableSlots={availableSlots}
            yearDropdownRef={yearDropdownRef}
            formatDate={formatDisplayDate}
            handleStartDateConfirm={handleStartDateConfirm}
            handleStartDateCancel={handleStartDateCancel}
            handleEndDateConfirm={handleEndDateConfirm}
            handleEndDateCancel={handleEndDateCancel}
            getEndDate={getEndDate}
            weekdays={WEEKDAYS}
            months={MONTHS}
          />
        );

      case 4:
        return (
          <ScheduleInternalTransactionStepConfirm
            txnPayLoad={txnPayLoad}
            setTxnPayLoad={setTxnPayLoad}
            isConfidential={isConfidential}
            availableSlots={availableSlots}
            previewImages={previewImages}
          />
        );

      default:
        return null;
    }
  };

  /* --------------------------------- render ------------------------------- */
  return (
    <>
      {isTransactionLoading && (
        <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-z-index-fixed-ui bg-PopUp-BackDrop opacity-dark-opacity">
          <div>
            <Loader type={"bulk_upload"} />
            <p className="font-semibold text-font-lg text-text-primary">
              Validating the data
            </p>
          </div>
        </div>
      )}
      {isInternalAccountModalOpen && (
        <InternalAccountPopUp
          typepop="popup"
          flow="internalTxn"
          onClosepop={() => setIsInternalAccountModalOpen(false)}
          handleButtonClick={handleInternalAccountSelection}
          txnPayLoad={txnPayLoad}
          setTxnPayLoad={setTxnPayLoad}
          beneAccountId={txnPayLoad?.beneAccountId}
          setIsNeft={setIsNeft}
          setIsImps={{}}
          setIsRtgs={setIsRtgs}
          scheduleIntenralTxn
        />
      )}

      {isLabelModalOpen && (
        <div className="fixed left-left-base w-width-full top-top-base h-height-full bg-PopUp-BackDrop bg-opacity-dark-opacity flex items-center justify-center z-max-zindex">
          <CommonAssignLabelPopup
            headerTitle="Assign/unassign labels:"
            headersubtext="Labels will be used to generate statistics for the associated transactions."
            closehandleFunction={handleCloseLabelModal}
            closeisDisabled={false}
            labels={availableLabels}
            setLabels={setAvailableLabels}
            isLabelPopupOpen={isLabelModalOpen}
            setPayload={setTxnPayLoad}
            txnPayLoad={txnPayLoad}
            selectedLabelsCreate={selectedLabels}
            setSelectedLabelsCreate={setSelectedLabels}
            labelActionDispatchedRef={labelActionDispatchedRef}
            loading={isLabelLoading}
            setLoading={setIsLabelLoading}
            type="create"
          />
        </div>
      )}

      {isConfirmLeavePopupOpen && (
        <div
          className="fixed z-z-index-max flex items-center justify-center top-top-base left-left-base w-width-full h-height-full bg-PopUp-BackDrop bg-opacity-dark-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <SmallPopup
            headerTitle={globalText?.LEAVE_PAGE_WARNING_HEADER}
            headerFontWeight="semibold"
            headerFontColor="text-primary"
            textSize="font-lg"
            subtext={globalText?.EXIT_CURRENT_FLOW_NOTE}
            btnOneText={globalText?.STAY}
            btnOneHandleFunction={() => setIsConfirmLeavePopupOpen(false)}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneIsDisabled={false}
            btnOneHoverOption="white"
            btnTwoText={globalText?.LEAVE}
            btnTwoHandleFunction={() => {
              setIsConfirmLeavePopupOpen(false);
              navigate(-1);
            }}
            btnTwoEnabledBgColor="var(--negative-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={false}
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoHoverOption="black"
          />
        </div>
      )}

      {isSimilarModalOpen && (
        <div className="fixed top-left-base left-left-base w-screen h-screen z-z-index-max bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <ScheduleInternalTransactionViewAllPopup
            onClose={() => setIsSimilarModalOpen(false)}
            headerTitle="Similar transactions found"
            headerIcon={ICONS.ALERT}
            headerSubtitle="Transactions with similar details have already been created. Proceeding will result in a duplicate transaction."
            showFooter
            footerTitle="Proceed with creating this transaction?"
            showFooterButton
            footerButtonText="Proceed with caution"
            onFooterButtonClick={() => {
              setIsSimilarModalOpen(false);
              handleCreateScheduledTransaction();
            }}
            contentProps={{
              txnHistorytype: "internalScheduleCreateTxn",
              duplicateFilters: buildDuplicateCheckPayload,
              destination_account_number: txnPayLoad?.bankAccountNo,
              source_account_number: txnPayLoad?.ownerBankAccountNo,
              txnPayLoad,
            }}
          />
        </div>
      )}
      <div className="h-screen flex-col flex overflow-hidden">
        <div className="w-width-full hidden Qsmd:block h-navbar-breadcrumb-height Qlg:bg-transparent bg-primary-nav flex-shrink-0"></div>
        <div className="fixed Qsmd:relative left-left-base z-z-index-loader flex justify-center items-center flex-col lg:flex-col-reverse w-width-full lg:py-padding-base bottom-bottom-base top-auto lg:top-top-base lg:bottom-auto Qsmd:bg-transparent shrink-0 Qsmd:min-h-[calc(var(--stepper-height)+var(--icon-sm))] min-h-stepper-moble-height">
          <PrimaryStepper
            activeStep={activeStep}
            handleForwardAction={handleNext}
            handleBackwardAction={handleBack}
            dataArray={dataArray}
          />
        </div>
        {isMobile && (
          <div className="fixed top-top-base left-left-base mt-margin-base w-width-full rounded-bl-border-radius-md rounded-br-border-radius-md z-z-index-header bg-primary-nav shadow-box-shadow-bottom lg:hidden block">
            <TopNavComponent
              topNavHeader={globalText?.CREATE_SCHEDULE_TRANSACTION}
              topNavSubText={steps[activeStep] || ""}
              isFormattingNotRequired
            />
          </div>
        )}
        <div className="flex flex-col h-height-full items-center relative w-width-full flex-1 overflow-y-auto overflow-x-hidden">
          <div className="max-w-screen-Qlg mx-auto flex flex-col w-width-full">
            <div className="w-width-full">
              <div className="h-navbar-height Qsmd:hidden block"></div>
            </div>
            <div
              className={`w-width-full ${
                (activeStep === ZERO && stepDirection === "next") ||
                isStepAnimationComplete
                  ? ""
                  : stepAnimationClass
              }`}
            >
              {renderStep()}
            </div>
          </div>
        </div>
        <div className="h-stepper-moble-height w-width-full Qsmd:hidden block"></div>
      </div>
    </>
  );
};

export default ScheduleInternalTransactionCreate;
